import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import SaberCard from '../../components/SaberCard'
import StatusBadge from '../../components/StatusBadge'
import type { BaseCaseModelVO, BaseCaseMonthVO, BaseCaseYearVO } from '../../saber.types'
import KwhCell from '../../components/KwhCell'
import MoneyCell from '../../components/MoneyCell'
import PercentCell from '../../components/PercentCell'
import SignedKwhCell from '../../components/SignedKwhCell'
import { SortHeaderButton } from '../../components/SortHeaderButton'
import { columnHelp } from '../../columnHelp'
import { compareSortValues } from '../../sortUtils'
import { GROUP_SUB_ROW_CLASS } from '../detail/grouped'
import { tableBodyRowClass } from '../../tableRowStyles'
import type { StickyPanelProps } from '../../stickyPanel'
import {
  kwhModelDeltaColorClass,
  performancePctColorClass,
  CELL_COLOR
} from '../../cellColor'
import NullDash from '../../components/NullDash'

// Pro-Forma by Contract Year (v001 §10) — expandable year rows with chronological
// monthly sub-rows. Year-level sorting follows the §8 convention; sub-rows stay
// attached under their parent in fixed chronological order. SaberTable doesn't
// support attached sub-rows, so this renders its own <table> with the same
// header/indicator conventions.

/** Actual minus modeled kWh; null when either side is unknown. */
export const kwhDelta = (modeled: number | null, actual: number | null): number | null =>
  modeled == null || actual == null ? null : actual - modeled

/** Actual / modeled revenue × 100; null when not computable. */
export const performancePct = (modeledRevenue: number | null, actualRevenue: number | null): number | null =>
  modeledRevenue == null || modeledRevenue === 0 || actualRevenue == null
    ? null
    : (actualRevenue / modeledRevenue) * 100

/** §10 color coding: ≥97 emerald · ≥90 amber · <90 red. */
export const performanceClass = performancePctColorClass

/** Signed Δ kWh coloring: above model emerald, below model red. */
export const deltaClass = kwhModelDeltaColorClass

/** 'YYYY-MM-DD' → "Jul 2025" (local-safe: avoids UTC parsing of date-only strings). */
export const monthLabel = (ymd: string): string => {
  const [y, m] = ymd.split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return ymd
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

type SortKey =
  | 'year'
  | 'modeledKwh'
  | 'actualKwh'
  | 'deltaKwh'
  | 'modeledRevenue'
  | 'actualRevenue'
  | 'performance'
  | 'status'

interface SortState {
  key: SortKey
  direction: 'asc' | 'desc'
}

const SORT_VALUES: Record<SortKey, (y: BaseCaseYearVO) => string | number | null> = {
  year: (y) => y.contractYear,
  modeledKwh: (y) => y.modeledKwh,
  actualKwh: (y) => y.actualKwh,
  deltaKwh: (y) => kwhDelta(y.modeledKwh, y.actualKwh),
  modeledRevenue: (y) => y.modeledRevenue,
  actualRevenue: (y) => y.actualRevenue,
  performance: (y) => performancePct(y.modeledRevenue, y.actualRevenue),
  status: (y) => y.status
}

const COLUMNS: Array<{ key: SortKey; label: string; align: 'left' | 'right' | 'center' }> = [
  { key: 'year', label: 'Contract Year', align: 'left' },
  { key: 'modeledKwh', label: 'Modeled kWh', align: 'right' },
  { key: 'actualKwh', label: 'Actual kWh', align: 'right' },
  { key: 'deltaKwh', label: 'Δ kWh', align: 'right' },
  { key: 'modeledRevenue', label: 'Modeled Rev', align: 'right' },
  { key: 'actualRevenue', label: 'Actual Rev', align: 'right' },
  { key: 'performance', label: 'Performance %', align: 'right' },
  { key: 'status', label: 'Status', align: 'center' }
]

const alignClass = (align: 'left' | 'right' | 'center'): string =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

export interface ProFormaTableProps extends StickyPanelProps {
  years: BaseCaseYearVO[]
  months: BaseCaseMonthVO[]
  totals: BaseCaseModelVO['totals']
}

export const ProFormaTable: React.FC<ProFormaTableProps> = ({ years, months, totals, stickyOffset, stickyZIndex }) => {
  const [sort, setSort] = useState<SortState | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const monthsByYear = useMemo(() => {
    const map = new Map<number, BaseCaseMonthVO[]>()
    for (const m of months) {
      const list = map.get(m.contractYear) ?? []
      list.push(m)
      map.set(m.contractYear, list)
    }
    // Sub-rows are always chronological regardless of year-level sort (§10).
    for (const list of map.values()) {
      list.sort((a, b) => a.month.localeCompare(b.month))
    }
    return map
  }, [months])

  const sortedYears = useMemo(() => {
    if (sort == null) return years
    const value = SORT_VALUES[sort.key]
    return years
      .map((row, index) => ({ row, index }))
      .sort((x, y) => {
        const cmp = compareSortValues(value(x.row), value(y.row), sort.direction)
        return cmp !== 0 ? cmp : x.index - y.index
      })
      .map((d) => d.row)
  }, [years, sort])

  const toggleSort = (key: SortKey): void => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' }
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
    })
  }

  const toggleYear = (contractYear: number): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(contractYear)) {
        next.delete(contractYear)
      } else {
        next.add(contractYear)
      }
      return next
    })
  }

  const allExpanded = years.length > 0 && expanded.size === years.length
  const toggleAll = (): void => {
    setExpanded(allExpanded ? new Set() : new Set(years.map((y) => y.contractYear)))
  }

  return (
    <SaberCard
      title="Pro-Forma by Contract Year"
      stickyOffset={stickyOffset}
      stickyZIndex={stickyZIndex}
      actions={
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      }
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const active = sort?.key === col.key
                return (
                  <th
                    key={col.key}
                    scope="col"
                    title={columnHelp('proForma', col.key)}
                    aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={classnames(
                      'px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400',
                      alignClass(col.align)
                    )}
                  >
                    <SortHeaderButton
                      label={col.label}
                      align={col.align}
                      active={active}
                      direction={sort?.direction ?? 'asc'}
                      onClick={() => toggleSort(col.key)}
                    />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {sortedYears.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-400 dark:text-slate-500">
                  No contract years.
                </td>
              </tr>
            ) : (
              sortedYears.map((y, yearIndex) => {
                const yearMonths = monthsByYear.get(y.contractYear) ?? []
                const isOpen = expanded.has(y.contractYear)
                const delta = kwhDelta(y.modeledKwh, y.actualKwh)
                const perf = performancePct(y.modeledRevenue, y.actualRevenue)
                return (
                  <React.Fragment key={y.contractYear}>
                    <tr
                      onClick={() => toggleYear(y.contractYear)}
                      className={tableBodyRowClass(yearIndex, 'cursor-pointer')}
                    >
                      <td className={classnames('px-3 py-2', CELL_COLOR.primary)}>
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={`Toggle year ${y.contractYear}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleYear(y.contractYear)
                          }}
                          className="inline-flex items-center gap-2"
                        >
                          <span
                            aria-hidden
                            className={classnames(
                              'w-3 text-slate-400 transition-transform dark:text-slate-500',
                              isOpen ? 'rotate-90' : undefined
                            )}
                          >
                            ▸
                          </span>
                          <span className="font-medium">Year {y.contractYear}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                            {yearMonths.length} mo
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200">
                        <KwhCell value={y.modeledKwh} />
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200">
                        {y.actualKwh == null ? <NullDash /> : <KwhCell value={y.actualKwh} />}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {delta == null ? (
                          <NullDash />
                        ) : (
                          <SignedKwhCell value={delta} role="modelDelta" />
                        )}
                      </td>
                      <td className={classnames('px-3 py-2 text-right', CELL_COLOR.muted)}>
                        <MoneyCell value={y.modeledRevenue} fractionDigits={0} />
                      </td>
                      <td className={classnames('px-3 py-2 text-right', CELL_COLOR.muted)}>
                        {y.actualRevenue == null ? (
                          <NullDash />
                        ) : (
                          <MoneyCell value={y.actualRevenue} fractionDigits={0} role="money" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {perf == null ? (
                          <NullDash />
                        ) : (
                          <PercentCell
                            value={perf}
                            valueClassName={classnames('font-medium', performanceClass(perf))}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <StatusBadge status={y.status} />
                      </td>
                    </tr>
                    {isOpen &&
                      yearMonths.map((m) => {
                        // Actuals render as em dashes for FORECAST months (§10).
                        const showActuals = m.status !== 'FORECAST'
                        const mDelta = showActuals ? kwhDelta(m.modeledKwh, m.actualKwh) : null
                        const mPerf = showActuals ? performancePct(m.modeledRevenue, m.actualRevenue) : null
                        return (
                          <tr key={m.month} className={GROUP_SUB_ROW_CLASS}>
                            <td className={classnames('px-3 py-1.5', CELL_COLOR.muted)}>
                              <span aria-hidden className={classnames('pl-5 pr-2', CELL_COLOR.null)}>
                                └
                              </span>
                              {monthLabel(m.month)}
                            </td>
                            <td className={classnames('px-3 py-1.5 text-right', CELL_COLOR.muted)}>
                              {m.modeledKwh == null ? <NullDash /> : <KwhCell value={m.modeledKwh} />}
                            </td>
                            <td className={classnames('px-3 py-1.5 text-right', CELL_COLOR.muted)}>
                              {showActuals && m.actualKwh != null ? <KwhCell value={m.actualKwh} /> : <NullDash />}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              {mDelta == null ? (
                                <NullDash />
                              ) : (
                                <SignedKwhCell value={mDelta} role="modelDelta" />
                              )}
                            </td>
                            <td className={classnames('px-3 py-1.5 text-right', CELL_COLOR.muted)}>
                              {m.modeledRevenue == null ? (
                                <NullDash />
                              ) : (
                                <MoneyCell value={m.modeledRevenue} fractionDigits={0} />
                              )}
                            </td>
                            <td className={classnames('px-3 py-1.5 text-right', CELL_COLOR.muted)}>
                              {showActuals && m.actualRevenue != null ? (
                                <MoneyCell value={m.actualRevenue} fractionDigits={0} role="money" />
                              ) : (
                                <NullDash />
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              {mPerf == null ? (
                                <NullDash />
                              ) : (
                                <PercentCell
                                  value={mPerf}
                                  valueClassName={classnames('font-medium', performanceClass(mPerf))}
                                />
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              <StatusBadge status={m.status} />
                            </td>
                          </tr>
                        )
                      })}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-semibold dark:border-slate-600">
              <td className={classnames('px-3 py-2', CELL_COLOR.primary)}>
                Lifetime ({years.length}-yr modeled)
              </td>
              <td className={classnames('px-3 py-2 text-right', CELL_COLOR.primary)}>
                <KwhCell value={totals.modeledKwh} />
              </td>
              <td className="px-3 py-2" />
              <td className="px-3 py-2" />
              <td className={classnames('px-3 py-2 text-right', CELL_COLOR.primary)}>
                <MoneyCell value={totals.modeledRevenue} fractionDigits={0} role="money" />
              </td>
              <td className="px-3 py-2" />
              <td className="px-3 py-2" />
              <td className="px-3 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </SaberCard>
  )
}

export default ProFormaTable
